import * as React from 'react';
import { Session } from 'next-auth';
import NextNProgress from 'nextjs-progressbar';
import { theme } from '@ghq-abi/design-system';

import { AppAbility, createUserAbility } from '~/shared/auth/permissions';
import {
  AbilityProvider,
  Flags,
  FlagsProvider,
  NavProvider,
  SessionInfoProvider,
  ToastProvider,
  UserProfileProvider,
  useUserProfile
} from '~/shared/contexts';
import { useAuthInitialization } from '~/shared/hooks/useAuthInitialization';
import { useMenuRoutes } from '~/shared/hooks/useMenuRoutes';
import { UserProfile } from '~/shared/types/profile';

type AppVariant = 'app' | 'eib';

type AppProviderProps = {
  children: React.ReactNode;
  session: Session | undefined;
  abilityRules: AppAbility['_indexedRules'] | undefined;
  flags: Partial<Flags> | undefined;
  initialProfile?: UserProfile | null;
  variant: AppVariant;
  AppTemplate: React.ComponentType<{ children: React.ReactNode }>;
};

export function AppProvider({
  children,
  session,
  abilityRules,
  flags,
  initialProfile,
  variant,
  AppTemplate
}: AppProviderProps) {
  useAuthInitialization();

  return (
    <SessionInfoProvider sessionInfo={session}>
      <ToastProvider>
        <UserProfileProvider initialProfile={initialProfile}>
          <AbilityProviderWithProfile
            session={session}
            abilityRules={abilityRules}
            flags={flags}
            variant={variant}
            AppTemplate={AppTemplate}>
            {children}
          </AbilityProviderWithProfile>
        </UserProfileProvider>
      </ToastProvider>
    </SessionInfoProvider>
  );
}

function AbilityProviderWithProfile({
  children,
  session,
  abilityRules,
  flags,
  variant,
  AppTemplate
}: {
  children: React.ReactNode;
  session: Session | undefined;
  abilityRules: AppAbility['_indexedRules'] | undefined;
  flags: Partial<Flags> | undefined;
  variant: AppVariant;
  AppTemplate: React.ComponentType<{ children: React.ReactNode }>;
}) {
  const { profile } = useUserProfile();

  const ability = React.useMemo(() => {
    return createUserAbility(session?.user, abilityRules, profile?.profileType);
  }, [session?.user, abilityRules, profile?.profileType]);

  return (
    <AbilityProvider ability={ability}>
      <FlagsProvider flags={flags}>
        <MenuRoutesProvider variant={variant}>
          <AppTemplate>{children}</AppTemplate>
          <NextNProgress
            color={theme.colors.black.toString()}
            startPosition={0.3}
            stopDelayMs={200}
            height={3}
          />
        </MenuRoutesProvider>
      </FlagsProvider>
    </AbilityProvider>
  );
}

function MenuRoutesProvider({
  variant,
  children
}: {
  variant: AppVariant;
  children: React.ReactNode;
}) {
  const { menuRoutes, footerRoutes } = useMenuRoutes({
    variant
  });

  return (
    <NavProvider menuRoutes={menuRoutes} footerRoutes={footerRoutes}>
      {children}
    </NavProvider>
  );
}
